import { legacyCamelKey } from '../utils/legacyHostCompat';
import { bidBoardAuthHeaders } from './bidBoardAuth';
import {
  isIosNativeAccountRequiredPayload,
  isAccountRequiredPayload,
  throwAccountAwareError,
} from './hostWebAppRequiredAccess';
import type { CompanyFleetAssetSummary } from './jobsService';


const API_BASE = '/api/zia-forms/submissions';
const REVIEWERS_API = '/api/zia-forms/reviewers';
const COMPANY_OPTIONS_API = '/api/zia-forms/company-options';
const COMPANY_FLEET_ASSETS_API = '/api/zia-forms/company-fleet-assets';

export type ZiaFormsReviewStatus = 'pending_review' | 'reviewed' | 'needs_correction' | 'archived';

export interface ZiaFormsAttachment {
  id: string;
  submissionId: string;
  fieldId: string | null;
  originalFilename: string | null;
  mimeType: string;
  fileSize: number;
  caption: string | null;
  createdAt: string;
  url: string;
}

export interface ZiaFormsAuditEvent {
  id: string;
  eventType: string;
  metadata: Record<string, unknown>;
  actorUserId: string | null;
  actorUsername: string | null;
  createdAt: string;
}

export interface ZiaFormsCloudSubmission {
  jobMasterId?: string | null;
  id: string;
  templateId: string;
  title: string;
  categoryId: string;
  companyId?: string | null;
  companyName?: string | null;
  localRecordId: string | null;
  userId: string;
  username: string | null;
  submitterName?: string | null;
  submitterEmail?: string | null;
  submitterPhone?: string | null;
  submitterContactSnapshot?: Record<string, unknown> | null;
  status: string;
  reviewStatus: ZiaFormsReviewStatus;
  submittedAt: string;
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  archivedAt: string | null;
  finalizedAt?: string | null;
  finalizedBy?: string | null;
  lockedAt?: string | null;
  lockedReason?: string | null;
  correctionNote?: string | null;
  correctedFromSubmissionId?: string | null;
  revisionNumber?: number;
  locationLatitude?: number | null;
  locationLongitude?: number | null;
  locationAccuracy?: number | null;
  locationCapturedAt?: string | null;
  locationSource?: string | null;
  locked?: boolean;
  evidenceCount: number;
  values?: Record<string, string | string[]>;
  attachments?: ZiaFormsAttachment[];
  auditEvents?: ZiaFormsAuditEvent[];
  correctionRevisions?: ZiaFormsCloudSubmission[];
  correctedFromSubmission?: ZiaFormsCloudSubmission | null;
}

export interface ZiaFormsReviewer {
  id: string;
  companyId: string;
  userId: string;
  username: string | null;
  addedBy: string;
  addedByUsername: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ZiaFormsCompanyOption {
  id: string;
  name: string;
  role: 'owner' | 'linked_member';
  status: string;
  formsSecondaryRecipient?: string | null;
}

const REVIEWER_USER_ID_KEY = legacyCamelKey('UserId');
const REVIEWER_USERNAME_KEY = legacyCamelKey('Username');

function authHeaders() {
  const headers = bidBoardAuthHeaders();
  // FormData uploads need the browser-generated multipart boundary.
  delete headers['Content-Type'];
  return headers;
}

function jsonHeaders() {
  return {
    'Content-Type': 'application/json',
    ...authHeaders(),
  };
}

export class ZiaFormsServiceError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = 'ZiaFormsServiceError';
    this.status = status;
    this.payload = payload;
  }
}

async function parseJsonResponse(response: Response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    if (
      isIosNativeAccountRequiredPayload(payload, response.status)
      || isAccountRequiredPayload(payload, response.status)
    ) {
      throwAccountAwareError(payload, response.status, 'Zia Forms request failed');
    }
    throw new ZiaFormsServiceError(
      payload?.error || 'ZiaWellNav Forms request failed',
      response.status,
      payload,
    );
  }
  return payload;
}

export const ziaFormsService = {
  async createSubmission(data: {
    jobMasterId?: string | null;
    templateId: string;
    localRecordId: string;
    submittedAt: string | null;
    values: Record<string, string | string[]>;
    companyId?: string | null;
    correctedFromSubmissionId?: string | null;
    location?: {
      latitude: number;
      longitude: number;
      accuracy?: number | null;
      capturedAt?: string | null;
      source?: string;
    } | null;
  }): Promise<ZiaFormsCloudSubmission> {
    const response = await fetch(API_BASE, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    });
    const payload = await parseJsonResponse(response);
    return payload.submission;
  },

  async listMySubmissions(params?: { limit?: number; jobMasterId?: string }): Promise<ZiaFormsCloudSubmission[]> {
    const sp = new URLSearchParams();
    if (params?.jobMasterId) sp.set('jobMasterId', params.jobMasterId);
    if (params?.limit) sp.set('limit', String(params.limit));
    const query = sp.toString();
    const response = await fetch(`${API_BASE}/mine${query ? `?${query}` : ''}`, { headers: authHeaders() });
    const payload = await parseJsonResponse(response);
    return payload.submissions || [];
  },

  async listCompanyOptions(): Promise<ZiaFormsCompanyOption[]> {
    const response = await fetch(COMPANY_OPTIONS_API, { headers: authHeaders() });
    const payload = await parseJsonResponse(response);
    return payload.companies || [];
  },

  async listCompanyFleetAssets(companyId?: string): Promise<CompanyFleetAssetSummary[]> {
    const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    const response = await fetch(`${COMPANY_FLEET_ASSETS_API}${query}`, { headers: authHeaders() });
    const payload = await parseJsonResponse(response);
    return payload.assets || [];
  },

  async listSubmissions(params?: {
    limit?: number;
    reviewStatus?: ZiaFormsReviewStatus;
    templateId?: string;
  }): Promise<ZiaFormsCloudSubmission[]> {
    const sp = new URLSearchParams();
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.reviewStatus) sp.set('reviewStatus', params.reviewStatus);
    if (params?.templateId) sp.set('templateId', params.templateId);
    const response = await fetch(`${API_BASE}?${sp.toString()}`, { headers: authHeaders() });
    const payload = await parseJsonResponse(response);
    return payload.submissions || [];
  },

  async getSubmission(id: string): Promise<ZiaFormsCloudSubmission> {
    const response = await fetch(`${API_BASE}/${id}`, { headers: authHeaders() });
    const payload = await parseJsonResponse(response);
    return payload.submission;
  },

  async updateReview(id: string, reviewStatus: ZiaFormsReviewStatus, note?: string): Promise<ZiaFormsCloudSubmission> {
    const response = await fetch(`${API_BASE}/${id}/review`, {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify({ reviewStatus, note }),
    });
    const payload = await parseJsonResponse(response);
    return payload.submission;
  },

  async listReviewers(): Promise<ZiaFormsReviewer[]> {
    const response = await fetch(REVIEWERS_API, { headers: authHeaders() });
    const payload = await parseJsonResponse(response);
    return payload.reviewers || [];
  },

  async addReviewer(data: { signedUserId: string; signedUsername?: string }): Promise<ZiaFormsReviewer> {
    const response = await fetch(REVIEWERS_API, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        [REVIEWER_USER_ID_KEY]: data.signedUserId,
        [REVIEWER_USERNAME_KEY]: data.signedUsername,
      }),
    });
    const payload = await parseJsonResponse(response);
    return payload.reviewer;
  },

  async removeReviewer(signedUserId: string): Promise<void> {
    const response = await fetch(`${REVIEWERS_API}/${encodeURIComponent(signedUserId)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    await parseJsonResponse(response);
  },

  async exportAuditPackage(submission: ZiaFormsCloudSubmission): Promise<void> {
    const response = await fetch(`${API_BASE}/${submission.id}/audit-export`, { headers: authHeaders() });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload?.error || 'Unable to export Forms audit package');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${submission.templateId}-audit-${Date.now()}.zip`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  async uploadEvidencePhoto(
    submissionId: string,
    file: File,
    data?: { fieldId?: string | null; caption?: string; latitude?: number | null; longitude?: number | null },
  ): Promise<ZiaFormsAttachment> {
    const formData = new FormData();
    formData.append('photo', file);
    if (data?.fieldId) formData.append('fieldId', data.fieldId);
    if (data?.caption) formData.append('caption', data.caption);
    if (data?.latitude != null) formData.append('latitude', String(data.latitude));
    if (data?.longitude != null) formData.append('longitude', String(data.longitude));

    const response = await fetch(`${API_BASE}/${submissionId}/attachments`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    const payload = await parseJsonResponse(response);
    return payload.attachment;
  },

  async deleteEvidencePhoto(submissionId: string, attachmentId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${submissionId}/attachments/${attachmentId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    await parseJsonResponse(response);
  },

  async loadAttachmentPreviewUrl(attachment: ZiaFormsAttachment, signal?: AbortSignal): Promise<string> {
    // Never navigate to a protected URL or send credentials to a supplied external URL.
    const url = `${API_BASE}/${encodeURIComponent(attachment.submissionId)}/attachments/${encodeURIComponent(attachment.id)}`;
    const response = await fetch(url, { headers: authHeaders(), credentials: 'include', signal });
    if (!response.ok) {
      if (response.status === 401) throw new Error('Your session expired. Sign in again to view this attachment.');
      if (response.status === 403) throw new Error('Your account does not have access to this company attachment.');
      throw new Error('This attachment could not be loaded. Please retry.');
    }
    if (/json|text\/html/i.test(response.headers.get('Content-Type') || '')) throw new Error('The attachment is unavailable. Please retry.');
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
};
